import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import BranchFilterSelect from "components/BranchFilterSelect";

import useDepartmentData from "layouts/tables/data/departmentTable";

function Department() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedDepartment,
    setSelectedDepartment,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddDepartment,
    branches,
    canFilterByBranch,
    branchFilter,
    setBranchFilter,
  } = useDepartmentData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading departments...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Church Departments"
        subtitle="Create and manage the departments members can belong to."
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddDepartment}>
              New Department
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      {canFilterByBranch && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <BranchFilterSelect branches={branches} value={branchFilter} onChange={setBranchFilter} />
        </Box>
      )}

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search departments..."
        />
      </Card>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedDepartment?.id ? "Edit Department" : "Add Department"}
        onSave={handleEditSubmit}
        saveLabel={selectedDepartment?.id ? "Save Changes" : "Create Department"}
        maxWidth="xs"
      >
        <FormSection>
          <TextField
            label="Department Name"
            value={selectedDepartment?.departmentName || ""}
            onChange={(e) =>
              setSelectedDepartment({ ...selectedDepartment, departmentName: e.target.value })
            }
            fullWidth
            margin="normal"
            required
          />
          <TextField
            select
            label="Status"
            value={selectedDepartment?.status || "active"}
            onChange={(e) =>
              setSelectedDepartment({ ...selectedDepartment, status: e.target.value })
            }
            fullWidth
            margin="normal"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          {canFilterByBranch && (
            <TextField
              select
              label="Branch"
              value={selectedDepartment?.branchId || ""}
              onChange={(e) =>
                setSelectedDepartment({ ...selectedDepartment, branchId: e.target.value || null })
              }
              fullWidth
              margin="normal"
              helperText="Leave blank for a church-wide department"
            >
              <MenuItem value="">
                <em>Church-wide</em>
              </MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </FormSection>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedDepartment?.departmentName}" department?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default Department;
